import { CompareArrowsRounded, OpenInFullRounded } from "@mui/icons-material";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import type { LLMClassifierImplementation } from "models/LLMs/LLMClassifierImplementation";
import { llmClassifierVersionLabel } from "../../utils/llmClassifierVersionDisplay";

interface VersionOption {
    label: string;
    value: string;
}

interface Props {
    versions: LLMClassifierImplementation[];
    beforeVersion: LLMClassifierImplementation;
    afterVersion: LLMClassifierImplementation;
    onChange: (side: "before" | "after", versionId: string) => void;
    /** Omitted when the comparison is already using the whole screen. */
    onExpand?: () => void;
}

function toOption(version: LLMClassifierImplementation): VersionOption {
    return {
        label: llmClassifierVersionLabel(version.createdOn) ?? "Version",
        value: version.id,
    };
}

export function VersionComparisonHeader(props: Props) {
    const options = props.versions.map(toOption);

    return (
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <CompareArrowsRounded fontSize="small" sx={{ color: "gray.600" }} />
            <Typography fontSize="15px" fontWeight={600}>
                Comparing
            </Typography>
            <AcxSelectSingle
                id="version-comparison-before"
                valueField="value"
                labelField="label"
                options={options}
                key={`before-${props.beforeVersion.id}`}
                defaultValue={toOption(props.beforeVersion)}
                onChange={(option: VersionOption) =>
                    props.onChange("before", option.value)
                }
                width="200px"
            />
            <Typography color="gray.600">→</Typography>
            <AcxSelectSingle
                id="version-comparison-after"
                valueField="value"
                labelField="label"
                options={options}
                key={`after-${props.afterVersion.id}`}
                defaultValue={toOption(props.afterVersion)}
                onChange={(option: VersionOption) =>
                    props.onChange("after", option.value)
                }
                width="200px"
            />
            {props.onExpand && (
                <Tooltip title="Compare full screen">
                    <IconButton
                        size="small"
                        aria-label="Compare full screen"
                        onClick={props.onExpand}
                        sx={{ ml: "auto", color: "gray.600" }}
                    >
                        <OpenInFullRounded fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );
}
